import {
    RouteStepParams as PandoraRouteStepParams,
    ParsedTransactionInstruction,
    PrimarySwapParams,
    SwapType
} from "@loopscale/pandora-types";

import { AssetData } from "./metadata";
import { ExpectedLoanValues } from "./arguments";

export interface OrcaPositionParams {
    principalMint: string;
    positionPrincipalInput?: number;
    existingPosition?: boolean;
}

export interface OrcaRouteParams {
    type: "orca";
    tickUpper: number;
    tickLower: number;
    whirlpool: string;
    tokenAMint: string;
    tokenBMint: string;
    luts?: string[];
    swapType?: SwapType;
    primarySwapParams?: PrimarySwapParams;
    positionParams?: OrcaPositionParams;
}

export type LoopRouteStepParams = PandoraRouteStepParams | OrcaRouteParams;
export interface LoopVaultInfoExpanded extends LoopVaultInfo {
    maturityTimestamp?: number;
    protocols?: Protocol[];
    assetTagType?: LoopAssetTypeTag[];
    metadataTags?: LoopMetadataTag[];
    unwindReferenceMint?: string;
}

export interface LoopVaultInfo {
    collateralMint: string;
    principalMint: string;
    collateralDeposited: number;
    collateralApyPct: number;
    maxLeverage: number;
    name: string;
    maxLeveragedApyPct: number;
    totalPrincipalDeployed: number;
    totalPrincipalDeployedUsd: number;
    principalAmountAvailable: number;
    routeType?: LoopRouteType | null;
    tags?: LoopFilterTags[];
    collateralDepositedUsd: number;
    quoteFetchParams: LoopRouteStepParams;
    maxCollateralAmount?: number;
    aumLimit?: number;
    feVisible: boolean;
    wAvgLeverage?: number;
    wAvgApy?: number;
    oneWeekCollateralDepositsUsd?: number;
    creationTime?: number;
    featured?: boolean;
    metadata?: {
        disable?: string[];
        overview?: string;
        riskDescription?: string;
        riskDescriptionLink?: string;
        notifyFormUrl?: string;
        comingSoonBanner?: string;
        jito?: {
            open?: boolean;
            close?: boolean;
        };
    };
}

export interface MeteoraLoopMetadata {
    aTokenVault: string;
    bTokenVault: string;
    lpMint: string;
    lutAddress: string;
    lutAddresses: string;
    poolAddress: string;
    tokenAMint: string;
    tokenBMint: string;
    vaultA: string;
    vaultALp: string;
    vaultALpMint: string;
    vaultB: string;
    vaultBLp: string;
    vaultBLpMint: string;
}
/**
 * Loop tag enums for categorizing and filtering loop vaults. They are the JSON tags column returned from `loop_vaults` table
 *
 * Protocol: Used for filtering loops by their underlying protocol (e.g., Meteora, Exponent)
 * LoopFilterTags: Used for filtering loops by their characteristics (e.g., SOL-pegged, DEX LP)
 * LoopAssetTypeTag: Used to determine asset types for loops, particularly for old chart functionality (e.g., LST, LRT)
 */

export enum Protocol {
    Exponent = "Exponent",
    RateX = "RateX",
    Meteora = "Meteora",
    Sanctum = "Sanctum",
    FlashTrade = "Flash.Trade",
    Fragmetric = "Fragmetric",
    Adrastea = "Adrastea",
    Stabble = "Stabble",
    Solayer = "Solayer",
    Onre = "OnRe",
    Hylo = "Hylo",
    Oro = "Oro",
    Hastra = "Hastra"
}

export enum LoopFilterTags {
    Leverage = "Leverage",
    SolPegged = "SOL-Pegged",
    StablecoinPegged = "Stablecoin-Pegged",
    DexLp = "DEX LP",
    FixedYield = "Fixed Yield",
    Farming = "Farming",
    Rwa = "RWA",
    Directional = "Directional",
    CLMM = "Leveraged Market Making",
    FX = "FX"
}

export enum LoopAssetTypeTag {
    LST = "LST",
    LRT = "LRT"
}

export enum LoopMetadataTag {
    New = "New",
    Leverage = "Leverage",
    ComingSoon = "comingSoon"
}

export type LoopTag = LoopFilterTags | Protocol | LoopAssetTypeTag | LoopMetadataTag;

export enum LoopRouteType {
    Jup,
    Meteora,
    Stabble,
    Exponent,
    Flash,
    Adrena,
    Ratex,
    Orca,
    Securitize,
    Manual,
    LP
}

export interface LoopInfoParams {
    loopVaults?: string[];
    showHidden: boolean;
}

export interface RefinanceLedgerParams {
    ledgerIndex: number;
    durationIndex: number;
    assetIndexGuidance?: number[];
}

export interface IncreaseLeverageTransactionInput {
    principalRequested: BorrowPrincipalRequested[];
    depositCollateral: DepositCollateralInput[];
    cpiIxs?: ParsedTransactionInstruction[];
    cpiLuts?: string[];
    cpiSigners?: string[];
    loanAddress?: string;
    setupIxs?: ParsedTransactionInstruction[];
    setupSigners?: string[];
    unifySetup: boolean;
    isLoop?: boolean;
    /**
     * Optional: when set for an existing loan, backend will prepend a refinance instruction
     * before the flash-borrow lock/unlock flow.
     *
     * The strategy switch target is inferred from `principalRequested[0].strategy`.
     */
    refinanceParams?: RefinanceLedgerParams;
}

export interface BorrowPrincipalRequested {
    ledgerIndex: number;
    principalAmount: number;
    principalMint: string;
    strategy: string;
    durationIndex: number;
    expectedLoanValues: ExpectedLoanValues;
}

export type WeightMatrixUpdate = [number, number, number, number, number];

export const DEFAULT_WEIGHT_MATRIX: WeightMatrixUpdate = [1_000_000, 0, 0, 0, 0];

export interface DepositCollateralInput {
    collateralAmount: number;
    collateralAssetData: AssetData;
    loanCreationParams?: LoanCreationParams;
    weightMatrixUpdate?: WeightMatrixUpdate;
}

//Loan Creaion
export interface OrcaPositionCreateParams {
    upperTick: number;
    lowerTick: number;
    tickSpacing: number;
    whirlpool: string;
    tokenMintA: string;
    tokenMintB: string;
}

export type LoanCreationParams = "Default" | { OrcaPositionCreate: OrcaPositionCreateParams };

// LP Vault Types
export interface VaultMintLpParams {
    vault: string;
    principalAmount: number;
    minLpAmount: number;
}
